import { useState } from 'react';
import { renderInlineBold } from '../../utils/inlineBold';
import { CLAMP_CONCERN_CHARS, clampExcerpt } from './extractionConcernsClamp';
import styles from './ExtractionConcernsBanner.module.css';

/** Concerns shown while collapsed; the rest sit behind the toggle. */
export const COLLAPSED_CONCERN_COUNT = 3;

interface ExtractionConcernsBannerProps {
  concerns: string[];
  /** Header text; defaults to "AI flagged N concern(s) for review". */
  label?: string;
  /**
   * Render without own background/border/padding — for embedding inside an
   * existing warning banner (e.g. the Merlin extractor block in
   * OrderDetailModal) so the two concern surfaces share one implementation.
   */
  embedded?: boolean;
}

/**
 * The extraction-concerns list shown in EmailViewModal (standalone banner)
 * and OrderDetailModal's Merlin block (embedded). Long lists collapse to the
 * first few with a small chevron toggle sitting on the last visible line, so
 * the banner can't push the content below it out of view.
 */
const ExtractionConcernsBanner: React.FC<ExtractionConcernsBannerProps> = ({
  concerns,
  label,
  embedded = false,
}) => {
  const [expanded, setExpanded] = useState(false);
  // Indexes of individually un-clamped long bullets (see CLAMP_CONCERN_CHARS).
  const [expandedItems, setExpandedItems] = useState<Set<number>>(new Set());

  if (concerns.length === 0) return null;

  const collapsible = concerns.length > COLLAPSED_CONCERN_COUNT;
  const visible =
    collapsible && !expanded
      ? concerns.slice(0, COLLAPSED_CONCERN_COUNT)
      : concerns;
  const hiddenCount = concerns.length - visible.length;

  const header =
    label ??
    `AI flagged ${concerns.length} concern${concerns.length > 1 ? 's' : ''} for review`;

  const toggle = collapsible && (
    <button
      className={styles.toggle}
      onClick={() => setExpanded((v) => !v)}
      aria-label={
        expanded ? 'Show less' : `Show all ${concerns.length} concerns`
      }
      title={expanded ? 'Show less' : `Show all ${concerns.length} concerns`}
      type="button"
    >
      {!expanded && <span className={styles.moreCount}>+{hiddenCount}</span>}
      <svg
        className={`${styles.chevron} ${expanded ? styles.chevronOpen : ''}`}
        width="14"
        height="14"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2.5"
        aria-hidden="true"
      >
        <polyline points="6 9 12 15 18 9" />
      </svg>
    </button>
  );

  return (
    <div className={embedded ? styles.bannerEmbedded : styles.banner}>
      {/* label="" suppresses the header row — the host (e.g. the Merlin
          panel's "Merlin's questions" title) already labels the list. */}
      {header !== '' && (
        <div className={styles.header}>
          {!embedded && <span className={styles.icon}>&#9888;</span>}
          {header}
        </div>
      )}
      <ul className={styles.list}>
        {visible.map((concern, i) => {
          const clamped =
            concern.length > CLAMP_CONCERN_CHARS && !expandedItems.has(i);
          return (
            <li key={i}>
              {renderInlineBold(clamped ? `${clampExcerpt(concern)}…` : concern)}
              {concern.length > CLAMP_CONCERN_CHARS && (
                <button
                  type="button"
                  className={styles.moreToggle}
                  data-testid={`concern-more-${i}`}
                  onClick={() =>
                    setExpandedItems((prev) => {
                      const next = new Set(prev);
                      if (next.has(i)) next.delete(i);
                      else next.add(i);
                      return next;
                    })
                  }
                >
                  {clamped ? 'more' : 'less'}
                </button>
              )}
              {/* Chevron rides the final visible line instead of taking a
                  line of its own. */}
              {i === visible.length - 1 && toggle}
            </li>
          );
        })}
      </ul>
    </div>
  );
};

export default ExtractionConcernsBanner;
